import React, { Fragment, useState } from 'react';
import OutageHeaderSearchBar from '../OutageSearchBar/OutageSearchBar';
import DimOrFlickeringComp from '../dim-or-flickering/DimOrFlickeringComp';
import {
  OutageLookupParams,
  OutageLookupResponse,
  OutageLookUpType,
} from '../../../../__generated__/pge-types';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PGEButton from '../../../buttons/PGE-Button';
import { useTranslation } from '../../../../hooks/useTranslation';
import {
  OutageMarkerView,
  useOutageLeftPanelState,
} from '../../providers/OutageLeftPanelStateProvider';
import { useOutageLookUp } from '../../hooks/useOutageLookUp';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { useIsMobile } from '../../../../util/style-utils';
import colors from '../../../../themes/main-colors';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';

const useStyles = makeStyles((theme: Theme) => ({
  instructions: {
    padding: '1rem 1.5rem',
    fontSize: 18,
    background: colors.white,
  },
  accountNotFound: {
    padding: '1rem 1.5rem',
    fontSize: 18,
    background: colors.linenWhite,
  },
  accordion: {
    margin: '1rem 1.5rem',
  },
  accordionRoot: {
    boxShadow: 'none',
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    background: colors.white,
    minHeight: 0,
    '& .MuiAccordionSummary-content': {
      margin: '12px 0',
      alignItems: 'center',
    },
  },
  accordionTitle: {
    fontWeight: 600,
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    margin: 0,
  },
  accordionDetails: {
    display: 'block',
  },
  inputBox: {
    width: '100%',
  },
  helpBox: {
    background: colors.white,
    fontSize: 16,
    textAlign: 'center',
    margin: '1rem 1.5rem',
    padding: '10px',
  },
  actions: {
    padding: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    background: colors.riverBlue,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
    },
  },
  cancelBtn: {
    color: colors.sparkBlue,
    fontWeight: 600,
    background: 'transparent',
    boxShadow: 'none',
    fontSize: 18,
    width: '50%',
    '&:hover': {
      background: 'transparent',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  lookupBtn: {
    color: '#fff',
    width: '50%',
    fontWeight: 600,
    borderRadius: 24,
    fontSize: 18,
    background: colors.sparkBlue,
    padding: '10px 32px',
    '&:hover': {
      background: colors.sparkBlue,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: 15,
    },
  },
  lookupContent: {
    display: 'flex',
    alignItems: 'center',
    margin: '1rem 1.5rem',
    background: colors.white,
    padding: '1rem',
    gap: '1.5rem',
    width: 'inherit',
  },
  lookupText: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '10px',
  },
  outageHeaderSearchBarStyles: {
    border: `1px solid ${colors.silverSand}`,
    padding: '3px 0',
  },
}));

export const ReportOutageOtherLocationDetails = () => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const {
    multiPremiseInfo,
    setOutageMarkerView,
    setReportOutageOpen,
    accountNotFoundInfo,
    setAccountNotFoundInfo,
    setMultiPremiseInfo,
    setSinglePremiseInfo,
    setHouseNumber,
    setLookUpCompleted,
  } = useOutageLeftPanelState();
  const isMobile = useIsMobile();
  const lookUpType = accountNotFoundInfo?.lookupType;
  const lookUpValue = accountNotFoundInfo?.lookupValue;
  const [formData, setFormData] = React.useState<
    OutageLookupParams | undefined
  >(undefined);
  const { wrapWithLoader } = useWrapWithLoader();
  const { getOutageLookUpQueryResponse } = useOutageLookUp();
  const { setPage } = useOutageResponsivePageTracker();
  const [searchList, setSearchList] = useState<string[]>([]);

  const lookUpTypeMappings = (lookupType: string): string => {
    switch (lookupType) {
      case OutageLookUpType.Phone:
        return 'Phone';
      case OutageLookUpType.Account:
        return 'Account';
      case OutageLookUpType.Premise:
        return 'Premise';
      default:
        return '';
    }
  };
  const handleLookUp = wrapWithLoader(async () => {
    setLookUpCompleted(true);
    if (formData?.value && !searchList.includes(formData?.value)) {
      setSearchList([...searchList, formData?.value]);
    }
    const response = await getOutageLookUpQueryResponse(
      formData as OutageLookupParams,
    );
    if (
      response &&
      response.isEligibleToReport !== null &&
      response.isEligibleToReport !== undefined
    ) {
      setAccountNotFoundInfo(undefined);
      setReportOutageOpen(false);
      if (
        !response.isAccountExists ||
        (response.isAccountExists &&
          !response?.houseNumber &&
          !response.multiplePremiseList)
      ) {
        const currentAccountNotFound = accountNotFoundInfo;
        setAccountNotFoundInfo({
          ...currentAccountNotFound,
          accountNotFoundModalOpen: true,
          lookupType: formData?.lookupType || OutageLookUpType.Account,
          lookupValue: formData?.value || undefined,
        });
      } else if (
        response.isAccountExists &&
        response?.houseNumber &&
        !response.multiplePremiseList
      ) {
        const houseNumber = response?.houseNumber ?? '';
        const premiseInfo = response?.outages?.[0];
        setMultiPremiseInfo(undefined);
        setSinglePremiseInfo({
          houseNumber,
          premiseDetails: premiseInfo,
          selectedPremiseEncryptedValue:
            response?.encryptedPremiseId ?? undefined,
          encryptedAccountNumber: response?.encryptedAccountNumber ?? undefined,
          encryptedServicePointList: response?.outages
            ?.map(outage => outage.encryptedServicePointId)
            .filter((id): id is string => !!id),
        });

        if (response?.outages?.[0]?.hasKnownOutage) {
          setOutageMarkerView(OutageMarkerView.SearchedLocation);
        } else {
          setOutageMarkerView(OutageMarkerView.NoOutage);
        }
        setPage(ResponsivePageView.Details);
      } else {
        if ((response?.multiplePremiseList?.length ?? 0) > 1) {
          setMultiPremiseInfo({
            ...multiPremiseInfo,
            isMultiPremiseAccount: true,
            MultiPremiseList: response.multiplePremiseList,
            isMultiSelectModalOpen: true,
            selectedPremiseDetails: multiPremiseInfo?.selectedPremiseDetails,
            selectedPremiseEncryptedValue:
              multiPremiseInfo?.selectedPremiseEncryptedValue ?? '',
          });
        }
      }
    }
  });

  return (
    <Fragment>
      {lookUpValue ? (
        <Typography className={classes.accountNotFound}>
          <p style={{ fontWeight: 600 }}>
            {richT('LOOKUP_HEADER', {
              LOOKUPTYPE: lookUpTypeMappings(lookUpType || ''),
              LOOKUPVALUE: lookUpValue,
            })}
          </p>
          <p> {richT('LOOKUP_CONTENT')} </p>
        </Typography>
      ) : (
        <Typography className={classes.instructions}>
          <p> {richT('REPORT_OUTAGE_NOT_YOURS')} </p>
        </Typography>
      )}
      <div className={classes.accordion}>
        <Accordion className={classes.accordionRoot}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
          >
            <p className={classes.accordionTitle}>
              <img
                style={{
                  maxWidth: 45,
                  paddingRight: '10px',
                }}
                src={require('../../../../images/DimOrFlickering.svg')}
                alt="DimOrFlickeringImage"
              />
              {t('DIM_FLICKERING_TOOLTIP')}
            </p>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <Divider />
            <DimOrFlickeringComp orangeButton={true} />
          </AccordionDetails>
        </Accordion>
      </div>

      <div className={classes.lookupContent}>
        {!isMobile && (
          <img
            style={{
              maxWidth: 280,
              paddingRight: '10px',
            }}
            src={require('../../../../images/Report_Outage_Home_New.png')}
            alt="reportOutage"
          />
        )}
        <Box className={classes.inputBox}>
          <Typography className={classes.lookupText}>
            {t('LOOKUP_PHONE_ACCOUNT_PREMISE')}
          </Typography>
          <Box className={isMobile ? '' : classes.outageHeaderSearchBarStyles}>
            <OutageHeaderSearchBar
              outageLookUpResult={(data: OutageLookupResponse) => {
                setHouseNumber(data.houseNumber ?? '');
              }}
              setFormData={(data: OutageLookupParams) => {
                setLookUpCompleted(false);
                setFormData(data);
              }}
            />
          </Box>
        </Box>
      </div>
      <div className={classes.helpBox}>
        <p>{richT('LOOKUP_QUESTIONS')}</p>
      </div>
      <div className={classes.actions}>
        <Button
          size="large"
          variant="text"
          className={classes.cancelBtn}
          onClick={() => {
            setAccountNotFoundInfo?.({
              ...(accountNotFoundInfo ?? {}),
              accountNotFoundModalOpen: false,
              lookupType: OutageLookUpType.Account,
              lookupValue: undefined,
            });
            setReportOutageOpen(false);
          }}
        >
          {t('CANCEL')}
        </Button>
        <PGEButton
          size="large"
          color="secondary"
          variant="contained"
          className={classes.lookupBtn}
          disabled={
            !formData ||
            !(
              (formData.lookupType === OutageLookUpType.Phone &&
                formData.value?.length === 14) ||
              (formData.lookupType === OutageLookUpType.Account &&
                formData.value?.length === 10) ||
              (formData.lookupType === OutageLookUpType.Premise &&
                formData.geoLocationToken &&
                formData.encryptedValue)
            )
          }
          onClick={() => handleLookUp()}
        >
          {t('LOOKUP')}
        </PGEButton>
      </div>
    </Fragment>
  );
};
